import { z } from 'zod';

import { translationTextSchema } from '../../i18n/main';
import { isDefined } from '../../utils';
import { createGetExtraDataParsedHelper } from '../nfctron-global';

/**
 * Schema for links displayed in the cart
 * Defines the structure for custom links shown to users
 */
export const eventExtraDataLinkSchema = z.object({
	label: z.string().min(1).describe('Label of the link displayed to users'),
	href: z.string().url().describe('URL destination when the link is clicked'),
});

/**
 * Schema for on-site payment configuration
 * Defines visual and descriptive elements for on-site payment options
 */
export const eventExtraDataOnSitePaymentSchema = z.object({
	type: z.enum(['card-chip', 'card-cash', 'chip', 'chip-ticket']).describe('Preset type of the on-site payment method'),
	label: translationTextSchema.optional().describe('Multi-language label for the on-site payment tag'),
	description: translationTextSchema.optional().describe('Multi-language description shown in the payment method detail sheet'),
});

/**
 * Schema for returnable cups control configuration
 * Defines settings for managing returnable cups functionality
 */
export const eventExtraDataWithReturnableCupsControlSchema = z.object({
	maximumCups: z.number().describe('Maximum number of returnable cups allowed per customer'),
	label: translationTextSchema.optional().describe('Multi-language label for the returnable cups control UI element'),
	description: translationTextSchema.optional().describe('Multi-language description explaining the returnable cups system'),
});

/**
 * Schema for zone control configuration
 * Defines settings for zone-based access management
 */
export const eventExtraDataWithZoneControlSchema = z.object({
	label: translationTextSchema.optional().describe('Multi-language label for the zone control UI element'),
	description: translationTextSchema.optional().describe('Multi-language description explaining the zone control system'),
});

/**
 * Schema for contact information displayed in the cart
 * Defines different types of contact information for event organizers and support
 */
export const eventExtraDataContactSchema = z.object({
	type: z
		.enum(['organizerEmail', 'organizerPhone', 'supportEmail', 'supportPhone', 'email', 'phone'])
		.describe('Category of the contact information'),
	value: z.string().min(1).describe('Actual contact value (email address or phone number)'),
	label: z.string().optional().describe('Custom label to display with the contact information'),
});

/**
 * Schema for GoOut ticket import provider configuration
 * Defines settings for importing tickets from GoOut platform
 */
export const eventExtraDataTicketImportProvidersGooutSchema = z.object({
	activeFrom: z
		.string()
		.datetime()
		.or(z.date())
		.transform((v) => new Date(v))
		.describe('Start date when this import provider becomes available'),
	activeTo: z
		.string()
		.datetime()
		.or(z.date())
		.transform((v) => new Date(v))
		.describe('End date when this import provider stops being available'),
	allowedWithSale: z.boolean().default(false).describe('Whether this provider can be used alongside regular ticket sales'),
	regex: z.string().optional().describe('Validation pattern for ticket codes from this provider'),
});

/**
 * Schema for NFCtron ticket import provider configuration
 * Defines settings for importing tickets from NFCtron platform
 */
export const eventExtraDataTicketImportProvidersNfctronSchema = z.object({
	activeFrom: z
		.string()
		.datetime()
		.or(z.date())
		.transform((v) => new Date(v))
		.describe('Start date when this import provider becomes available'),
	activeTo: z
		.string()
		.datetime()
		.or(z.date())
		.transform((v) => new Date(v))
		.describe('End date when this import provider stops being available'),
	allowedWithSale: z.boolean().default(true).describe('Whether this provider can be used alongside regular ticket sales'),
	regex: z.string().optional().describe('Validation pattern for ticket codes from this provider'),
});

/**
 * Schema for additional tracking IDs
 * Defines structure for various analytics and tracking services
 */
export const additionalTrackingIdSchema = z.object({
	provider: z.enum(['gtm', 'fbp', 'ttq', 'sklik']).describe('Analytics or tracking service provider identifier'),
	id: z.string().min(1).describe('Tracking ID or pixel ID for the specified provider'),
	/** TODO: potentially other configuration, but not for now */
});

/**
 * Schema for navbar logo configuration
 * Defines customization options for the logo in navigation bar
 */
export const eventExtraDataNavbarLogoSchema = z.object({
	logoUrl: z.string().url().optional().describe('URL of the custom logo image to display'),
	height: z.number().optional().describe('Custom height for the logo (overrides default sizing)'),
	width: z.number().optional().describe('Custom width for the logo (overrides default sizing)'),
	backUrl: z.string().url().optional().describe('Destination URL when users click on the logo'),
});

/**
 * Schema for organizer partner configuration (coupons)
 * Defines settings for partner/discount code functionality
 */
export const eventExtraDataOrgPartnerConfigSchema = z.object({
	/** Whether the cart displays a button/input to enter a partner code */
	isInputShown: z.boolean().default(false).describe('Controls visibility of the partner code input field'),
	/** Limit of partner codes that can be entered, defaults to 1 but can allow more */
	allowMultipleLimit: z.number().default(1).describe('Maximum number of partner codes a user can apply'),
});

/**
 * Schema for benefit code provider configuration
 * Defines settings for specialized discount/benefit code systems
 */
export const eventExtraDataBenefitCodeProviderSchema = z.object({
	providerId: z.string().min(1).describe('Unique identifier for the benefit code provider'),
	name: z.string().min(1).describe('Display name of the benefit code provider'),
	prefix: z.string().optional().describe('Optional prefix used in codes from this provider'),
	logoUrl: z.string().url().optional().describe("URL to the provider's logo image"),
	color: z.string().optional().describe('Brand color for UI elements related to this provider'),
	buttonText: translationTextSchema.optional().describe("Multi-language text for the provider's action button"),
	buttonLogoUrl: z.string().url().optional().describe("URL to an icon shown on the provider's button"),
	sheetTitle: translationTextSchema.optional().describe("Multi-language title for the provider's input sheet"),
	sheetSubmitButtonLabel: translationTextSchema.optional().describe('Multi-language label for the submit button'),
	sheetInputLabel: translationTextSchema.optional().describe('Multi-language label for the code input field'),
	sheetInputDescription: translationTextSchema.optional().describe('Multi-language helper text explaining the code input'),
	sheetInputPlaceholder: translationTextSchema.optional().describe('Multi-language placeholder text for the input field'),
});

/**
 * Schema for Sklik IDs configuration
 * Defines settings for conversion and retargeting tracking
 */
export const eventExtraDataSklikIdsSchema = z.union([
	z.object({
		conversionId: z.string().describe('Sklik conversion id'),
		eventType: z.enum(['purchase']).describe('Event type this conversion id is for'),
		retargetingId: z.never().optional(),
	}),
	z.object({
		retargetingId: z.string().describe('Sklik retargeting id'),
		conversionId: z.never().optional(),
		eventType: z.never().optional(),
	}),
]);

/**
 * Schema for mobile unsupported configuration
 * @deprecated No longer used in current implementation
 */
export const eventExtraDataMobileUnsupportedSchema = z.object({
	reason: z.number(),
	version: z.string().min(1),
});

/**
 * Schema for partner tickets configuration
 * @deprecated No longer used in current implementation
 */
export const eventExtraDataPartnerTicketsSchema = z.object({
	title: z.string().optional(),
	text: z.string().optional(),
});

/**
 * Cart extra data schema without defaults
 * Defines all possible configuration options for the cart experience
 */
export const eventExtraDataWithoutDefaultsSchema = z.object({
	/** event links */
	links: z.array(eventExtraDataLinkSchema).describe('Custom links displayed in the cart footer or info section'),
	/** event specifics – onsite payment type */
	onSitePayment: eventExtraDataOnSitePaymentSchema.nullish().describe('Configuration for on-site payment options and information'),
	/** event specifics – returnable cups control */
	withReturnableCupsControl: eventExtraDataWithReturnableCupsControlSchema.nullish().describe('Configuration for returnable cups deposit system'),
	/** event specifics – zone control */
	withZoneControl: eventExtraDataWithZoneControlSchema.nullish().describe('Configuration for zone-based access control features'),
	/** contacts */
	contacts: z.array(eventExtraDataContactSchema).describe('Contact information for event organizers and support'),
	/** ticket import providers configuration */
	gooutImportProvider: eventExtraDataTicketImportProvidersGooutSchema.nullish().describe('Settings for importing tickets from GoOut platform'),
	nfctronImportProvider: eventExtraDataTicketImportProvidersNfctronSchema.nullish().describe('Settings for importing tickets from NFCtron platform'),
	/** TikTok Pixel tracking ID */
	ttqPixelId: z.string().optional().describe('TikTok Pixel ID for conversion tracking'),
	/** additional tracking Ids */
	additionalTrackingIds: z.array(additionalTrackingIdSchema).describe('Collection of tracking IDs for various analytics platforms'),
	/** OpenGraph data */
	ogImageUrl: z.string().optional().describe('Image URL for social media sharing previews'),
	ogTitle: z.string().optional().describe('Title for social media sharing previews'),
	ogDescription: z.string().optional().describe('Description for social media sharing previews'),
	/** navbar logo */
	navbarLogo: eventExtraDataNavbarLogoSchema.nullish().describe('Custom logo configuration for the navigation bar'),
	/** organizer partner configuration (aka coupons) */
	orgPartner: eventExtraDataOrgPartnerConfigSchema.nullish().describe('Settings for partner/discount code functionality'),
	/** countdown date */
	countdownDate: z.string().nullish().describe('Target date for displaying a countdown timer'),
	/** url favicon */
	faviconUrl: z.string().url().optional().describe('Custom favicon URL for the cart page'),
	/** hide organizer in cart event detail */
	hideOrganizerInCart: z.boolean().optional().describe('Option to hide organizer information in the cart view'),
	/** specific benefit code providers */
	benefitCodeProviders: z.array(eventExtraDataBenefitCodeProviderSchema).describe('Configuration for specialized discount/benefit code systems'),
	/** Sklik tracking ID */
	sklik: z.array(eventExtraDataSklikIdsSchema).describe('Sklik IDs for conversion and retargeting tracking'),

	/* --- deprecated stuff--- */
	/** @deprecated partner tickets */
	partnerTickets: eventExtraDataPartnerTicketsSchema.nullish(),
	/** @deprecated mobile unsupported settings */
	iosMobileUnsupported: eventExtraDataMobileUnsupportedSchema.nullish(),
	/** @deprecated mobile unsupported settings */
	androidMobileUnsupported: eventExtraDataMobileUnsupportedSchema.nullish(),
});
export const eventExtraDataWithoutDefaultsEmpty = eventExtraDataWithoutDefaultsSchema.parse({
	links: [],
	contacts: [],
	additionalTrackingIds: [],
	benefitCodeProviders: [],
	sklik: [],
} satisfies z.input<typeof eventExtraDataWithoutDefaultsSchema>);

/**
 * Cart extra data schema with defaults
 * Extends the base schema with default values for required fields
 */
export const eventExtraDataWithDefaultsSchema = eventExtraDataWithoutDefaultsSchema.merge(
	z.object({
		links: eventExtraDataWithoutDefaultsSchema.shape.links.default([]),
		contacts: eventExtraDataWithoutDefaultsSchema.shape.contacts.default([]),
		additionalTrackingIds: eventExtraDataWithoutDefaultsSchema.shape.additionalTrackingIds.default([]),
		benefitCodeProviders: eventExtraDataWithoutDefaultsSchema.shape.benefitCodeProviders.default([]),
		sklik: eventExtraDataWithoutDefaultsSchema.shape.sklik.default([]),
	}),
);
export const eventExtraDataWithDefaultsEmpty = eventExtraDataWithDefaultsSchema.parse({} satisfies z.input<typeof eventExtraDataWithDefaultsSchema>);

export type EventExtraData = z.infer<typeof eventExtraDataWithDefaultsSchema>;

/**
 * Helper for parsing cart extra data
 * Handles both versions with and without defaults, and provides backward compatibility
 */
export const getEventExtraDataParsed = createGetExtraDataParsedHelper({
	schemaWithDefaults: eventExtraDataWithDefaultsSchema,
	schemaWithoutDefaults: eventExtraDataWithoutDefaultsSchema,
	defaultData: eventExtraDataWithDefaultsEmpty,
	/** polyfills due to backwards compatibility */
	polyfillFn(data) {
		/** flattened ticketImportProviders */
		if (isDefined(data.ticketImportProviders)) {
			data.gooutImportProvider = data.ticketImportProviders.goout;
			data.nfctronImportProvider = data.ticketImportProviders.nfctron;
		}
		return data;
	},
});
